import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  Settings, 
  Heart, 
  Eye, 
  TrendingUp,
  Plus,
  Filter,
  Grid,
  List,
  Edit,
  Copy,
  ExternalLink
} from 'lucide-react';
import NFTCard from '../components/NFTCard';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('owned');
  const [viewMode, setViewMode] = useState('grid');

  const userProfile = {
    address: '0x0C0CCc31995042e9B913B357a6eaBdBa591233E7',
    username: 'SupremeCreator',
    bio: 'Digital artist and NFT collector passionate about the future of blockchain art.',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
    stats: {
      owned: 12,
      created: 8,
      sold: 3,
      volume: '2.4 ETH'
    }
  };

  const ownedNFTs = [
    {
      id: 1,
      name: "Supreme Genesis #1",
      image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&h=400&fit=crop",
      price: "0.1",
      currency: "ETH",
      creator: "0x1234...5678",
      likes: 42,
      views: 128
    },
    {
      id: 2,
      name: "Digital Crown #5",
      image: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop",
      price: "0.25",
      currency: "ETH",
      creator: "0x8765...4321",
      likes: 38,
      views: 95
    },
    {
      id: 3,
      name: "Royal Artifact #12",
      image: "https://images.unsplash.com/photo-1642790105077-9c5a4a5b5b5b?w=400&h=400&fit=crop",
      price: "0.5",
      currency: "SUP",
      creator: "0xabcd...efgh",
      likes: 67,
      views: 203
    }
  ];

  const createdNFTs = [
    {
      id: 4,
      name: "My Creation #1",
      image: "https://images.unsplash.com/photo-1634017839464-5c339ebe3cb4?w=400&h=400&fit=crop",
      price: "0.15",
      currency: "ETH",
      creator: userProfile.address,
      likes: 29,
      views: 87
    },
    {
      id: 5,
      name: "My Creation #2",
      image: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop",
      price: "0.3",
      currency: "ETH",
      creator: userProfile.address,
      likes: 45,
      views: 156
    }
  ];

  const tabs = [
    { id: 'owned', label: 'Owned', count: userProfile.stats.owned },
    { id: 'created', label: 'Created', count: userProfile.stats.created },
    { id: 'favorites', label: 'Favorites', count: 5 },
    { id: 'activity', label: 'Activity', count: null }
  ];

  const copyAddress = () => {
    navigator.clipboard.writeText(userProfile.address);
    // You could add a toast notification here
  };

  return (
    <div className="min-h-screen py-8">
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="max-w-6xl mx-auto"
      >
        {/* Profile Header */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Avatar */}
            <div className="relative">
              <img
                src={userProfile.avatar}
                alt="Profile"
                className="w-24 h-24 rounded-full border-4 border-purple-500/50"
              />
              <div className="absolute -bottom-2 -right-2 bg-green-500 w-6 h-6 rounded-full border-2 border-white/20"></div>
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <h1 className="text-3xl font-bold text-white">{userProfile.username}</h1>
                <div className="flex gap-2">
                  <button
                    onClick={copyAddress}
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all duration-200"
                  >
                    <Copy className="h-4 w-4" />
                    {userProfile.address.slice(0, 6)}...{userProfile.address.slice(-4)}
                  </button>
                  <button className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-all duration-200">
                    <ExternalLink className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6">{userProfile.bio}</p>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{userProfile.stats.owned}</p>
                  <p className="text-gray-400 text-sm">Owned</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{userProfile.stats.created}</p>
                  <p className="text-gray-400 text-sm">Created</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{userProfile.stats.sold}</p>
                  <p className="text-gray-400 text-sm">Sold</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{userProfile.stats.volume}</p>
                  <p className="text-gray-400 text-sm">Volume</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center">
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </button>
              <button className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg transition-all duration-200">
                <Settings className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white/5 backdrop-blur-sm rounded-2xl p-2 border border-white/10 mb-8"
        >
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-purple-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <span>{tab.label}</span>
                {tab.count !== null && (
                  <span className="bg-white/20 text-xs px-2 py-1 rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {activeTab === 'owned' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Owned NFTs</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === 'grid' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Grid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === 'list' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                  : 'grid-cols-1'
              }`}>
                {ownedNFTs.map((nft, index) => (
                  <motion.div
                    key={nft.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <NFTCard nft={nft} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'created' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Created NFTs</h2>
                <button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 flex items-center">
                  <Plus className="h-4 w-4 mr-2" />
                  Create New
                </button>
              </div>
              
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                  : 'grid-cols-1'
              }`}>
                {createdNFTs.map((nft, index) => (
                  <motion.div
                    key={nft.id}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                  >
                    <NFTCard nft={nft} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'favorites' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Favorite NFTs</h2>
              <div className="text-center py-16">
                <Heart className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No favorites yet</h3>
                <p className="text-gray-400 mb-6">Start exploring and heart your favorite NFTs</p>
                <button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200">
                  Explore Marketplace
                </button>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-6">Activity</h2>
              <div className="space-y-4">
                {[
                  { action: 'Minted', nft: 'Supreme Genesis #1', time: '2 hours ago', type: 'mint' },
                  { action: 'Sold', nft: 'Digital Crown #5', time: '1 day ago', type: 'sale' },
                  { action: 'Listed', nft: 'Royal Artifact #12', time: '3 days ago', type: 'list' },
                  { action: 'Bought', nft: 'Cosmic Warrior #23', time: '1 week ago', type: 'buy' }
                ].map((activity, index) => (
                  <motion.div
                    key={index}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${
                        activity.type === 'mint' ? 'bg-green-500' :
                        activity.type === 'sale' ? 'bg-blue-500' :
                        activity.type === 'list' ? 'bg-yellow-500' : 'bg-purple-500'
                      }`}></div>
                      <div>
                        <p className="text-white font-medium">{activity.action} {activity.nft}</p>
                        <p className="text-gray-400 text-sm">{activity.time}</p>
                      </div>
                    </div>
                    <TrendingUp className="h-5 w-5 text-gray-400" />
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Profile;
